import React from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  MuiThemeProvider,
  Tooltip,
  Typography,
  TypographyProps,
  withStyles,
  IconButton,
  OutlinedInput,
} from '@material-ui/core';
import { orderBy, cloneDeep, clone, without, isNil, isEmpty, groupBy, isEqual, deburr } from 'lodash';
import TemplateModalWithButton from 'src/components/TemplateModalWithButton/TemplateModalWithButton';
import viewConfiguratorStyle, {
  getItemStyle,
  getListStyle,
  configuratorAccordionOverrides,
  configuratorAccordionSummaryOverrides,
  configuratorAccordionDetailsOverrides,
  configuratorAccordionTypographyOverrides,
  resetButtonStyles,
} from './ViewConfiguratorModal.styles';
import { TenantConfigViewData, TenantConfigViewItem } from 'src/dao/tenantConfigClient';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import { Option } from '../Configure/ConfigureModal';
import { FavoriteListItemStorage } from 'src/components/Subheader/Favorites/Favorites.types';
import { GridApi } from '@ag-grid-community/core';
import SubheaderQuickActionButton from '../Subheader/SubheaderQuickActionButton';
import { muiTheme } from 'src/utils/Style/Theme';
import { ExpandMore, Close } from '@material-ui/icons';
import { SortByDirection } from 'src/components/Subheader/Subheader.types';
import ServiceContainer from 'src/ServiceContainer';
import { flattenConfigItems } from '../ConfigurableGrid/ConfigurableGrid';
import { classes } from 'typestyle';
import SquareActionButton from 'src/common-ui/components/Buttons/SquareActionButton';

export interface CompanionFavoriteData {
  companionSortDirection: SortByDirection;
  companionSortField: string | null;
  companionLevelField?: string | null;
}

export interface ViewConfiguratorModalProps {
  viewConfig: TenantConfigViewData;
  getGridApi?: () => GridApi | undefined; // Get an up to date grid data
  unmodifiedViewDefn: TenantConfigViewData;
  companionData?: CompanionFavoriteData;
  defaultCompanionData?: CompanionFavoriteData;
  configureSelections?: Option[];
  defaultConfigureSelections?: Option[];
  showPinCheckboxForGrid?: boolean;
  subheaderTitle?: string;
  updateConfig?: (config: TenantConfigViewData) => void;
  updateConfiguration?: (configurationSelections: Option[]) => void;
  onSubmit?: (config: TenantConfigViewData) => void;
}

export interface ViewConfiguratorModalState {
  showResetConfirmation: boolean;
  viewConfig: TenantConfigViewItem[];
  onOpenViewConfig: TenantConfigViewItem[];
  openOverride?: boolean;
  searchValue: string;
}

export interface LocalStorageConfig extends FavoriteListItemStorage {}

export interface StyledTypographyProps extends TypographyProps {
  hasIcon: boolean;
}

const StyledAccordion = withStyles(() => configuratorAccordionOverrides)(Accordion);
const StyledAccordionSummary = withStyles(() => configuratorAccordionSummaryOverrides)(AccordionSummary);
const StyledAccordionDetails = withStyles(() => configuratorAccordionDetailsOverrides)(AccordionDetails);
const StyledTypography = withStyles(() => configuratorAccordionTypographyOverrides)(Typography);

/** Function to apply grouping of views with groupingKey (ConfigurableGrid) */
const applyGroupingInPlace = (items: TenantConfigViewItem[]): TenantConfigViewItem[] => {
  // if we already have nested columns, *don’t rebuild* groups—
  // but *do* fill in any undefined dataIndex on the headers because it messes up selection otherwise
  if (items.some((item) => Array.isArray(item.columns))) {
    return items.map((item, idx) => {
      if (Array.isArray(item.columns)) {
        return {
          ...item,
          // if dataIndex is falsy, give it a unique one
          dataIndex: item.dataIndex ?? `group-${item.groupingKey ?? 'header'}-${idx}`,
        };
      }
      return item;
    });
  }

  // build groupedColumns in one pass, grouping only items with same key that are adjacent
  const groupedColumns = items.reduce<TenantConfigViewItem[]>((groupedCols, current, idx, arr) => {
    const key = current.groupingKey || '';
    const prev = groupedCols[groupedCols.length - 1];
    // if we've already started a group for this key, add it in
    if (prev?.columns && prev.groupingKey === key) {
      prev.columns.push(current);
    } else if (!isEmpty(key)) {
      groupedCols.push({
        ...current, // base header on this first item for now
        dataIndex: `group-${key}-${idx}`,
        text: key, // show the groupingKey as header text & configurator label
        viewConfiguratorName: key,
        columns: [current], // start the contiguous run here
      });
      // otherwise, if the next item shares this key, start a new group header
    } else {
      groupedCols.push(current);
    }

    return groupedCols;
  }, []);

  // then fix each group-header’s visible/hidden based on its children
  return groupedColumns.map((col) => {
    if (col.columns) {
      // if any child is visible, the header should be visible
      const anyVisible = col.columns.some((c) => c.visible !== false && c.hidden !== true);
      return {
        ...col,
        visible: anyVisible,
        hidden: !anyVisible,
      };
    }
    return col;
  });
};
//this function to check if the keyGroupedColumns are not matching with current column grid
const hasColumnOrderChanged = (gridApi: GridApi, groupedConfig: TenantConfigViewItem[]): boolean => {
  const currentColumnState = gridApi.getColumnState();

  const flatConfig = flattenConfigItems(groupedConfig)
    .filter((col) => col.visible !== false && col.hideFromConfigurator !== true)
    .map((col) => col.id || col.dataIndex)
    .filter(Boolean);

  const visibleGridColIds = currentColumnState
    .filter((col) => !col.hide && flatConfig.includes(col.colId))
    .map((col) => col.colId);

  const filteredConfig = flatConfig.filter((id) => visibleGridColIds.includes(id));

  return !isEqual(filteredConfig, visibleGridColIds);
};

export default class ViewConfiguratorModal extends React.Component<
  ViewConfiguratorModalProps,
  ViewConfiguratorModalState
> {
  state: ViewConfiguratorModalState = {
    showResetConfirmation: false,
    viewConfig: [],
    onOpenViewConfig: [],
    searchValue: '',
  };

  // Check if config column contains children
  private hasNestedColumns = (): boolean =>
    this.state.viewConfig.some((item) => Array.isArray(item.columns) && item.columns.length > 0);

  /** Set visibility flags on an item (optionally propagating to children) without reordering.
   * Some configs still use `hidden` instead of `visible`, so keep both in sync here.
   */
  private setItemVisibility = (item: TenantConfigViewItem, isVisible: boolean, propagateChildren = false) => {
    const updated: TenantConfigViewItem = {
      ...item,
      visible: isVisible,
    };

    if ('hidden' in item) {
      updated.hidden = !isVisible;
    }

    if (item.columns) {
      updated.columns = item.columns.map((child) =>
        propagateChildren ? this.setItemVisibility(child, isVisible, true) : { ...child },
      );
    }

    return updated;
  };

  private getItemLabel = (item: TenantConfigViewItem): string => {
    return (item.viewConfiguratorName ?? item.text) || '';
  };

  private getSearchMatcher = (searchValue: string): { query: string; exact: boolean } | undefined => {
    const normalizedSearch = deburr(searchValue.trim());

    if (!normalizedSearch) {
      return undefined;
    }

    if (normalizedSearch.startsWith('=')) {
      const exactQuery = normalizedSearch.slice(1).trim().toLowerCase();
      return exactQuery ? { query: exactQuery, exact: true } : undefined;
    }

    return { query: normalizedSearch.toLowerCase(), exact: false };
  };

  private itemMatchesSearch = (item: TenantConfigViewItem, searchQuery: string, exact = false): boolean => {
    const itemLabel = deburr(this.getItemLabel(item)).toLowerCase();
    return exact ? itemLabel === searchQuery : itemLabel.includes(searchQuery);
  };

  private getFilteredLeafItems = (items: TenantConfigViewItem[]): TenantConfigViewItem[] => {
    return items.flatMap((item) => {
      if (item.columns && item.columns.length > 0) {
        return item.columns.filter((child) => !child.hideFromConfigurator);
      }
      return [item];
    });
  };

  private getConfigKey = (item?: TenantConfigViewItem): string | undefined => {
    return item ? (item.id ?? item.dataIndex ?? item.text ?? item.viewConfiguratorName) : undefined;
  };

  // Filtered actions should read "checked" only when every displayed child is visible.
  private itemVisibleInFilter = (item: TenantConfigViewItem): boolean => {
    if (item.columns && item.columns.length > 0) {
      return item.columns.every((child) => this.itemVisible(child));
    }

    return this.itemVisible(item);
  };

  private getFullConfigItem = (item: TenantConfigViewItem): TenantConfigViewItem | undefined => {
    return this.state.viewConfig.find((configItem) => configItem.dataIndex === item.dataIndex);
  };

  private getParentCheckboxState = (item: TenantConfigViewItem): { checked: boolean; indeterminate: boolean } => {
    // Three checks here:
    // 1. Check if doesn't have children and just return visible state
    // 2. If it has children, check if the parent has specific visible/hidden config and use that
    // 3. If it has children and no parent visible/hidden config use the children to calculate state

    // 1. no children
    if (!item.columns || item.columns.length === 0) {
      return {
        checked: this.itemVisible(item),
        indeterminate: false,
      };
    }

    // 2. Parent has specific config
    // this one uses `fullItem`, the same way that children are compared, so as to check if the
    // config shape has visible/hidden instead of the passed in item, which can be mutated
    const fullItem = this.getFullConfigItem(item) ?? item;
    if (!this.itemVisible(fullItem)) {
      return {
        checked: false,
        indeterminate: false,
      };
    }

    // 3. Parent has no specific config so base it off children state
    const fullChildren = (fullItem.columns ?? []).filter((child) => !child.hideFromConfigurator);
    const visibleChildrenCount = fullChildren.filter((child) => this.itemVisible(child)).length;

    return {
      checked: fullChildren.length > 0 && visibleChildrenCount === fullChildren.length,
      indeterminate: visibleChildrenCount > 0 && visibleChildrenCount < fullChildren.length,
    };
  };

  // Apply show/hide only to the currently filtered tree, then recompute each parent from its full child set.
  private applyVisibilityToFilteredItems = (
    baseConfig: TenantConfigViewItem[],
    filteredConfig: TenantConfigViewItem[],
    isVisible: boolean,
  ): TenantConfigViewItem[] => {
    if (filteredConfig.length === 0) {
      return baseConfig;
    }

    // Key by dataIndex+text so duplicate dataIndex columns (e.g. "Stylecolor" vs "Stylecolor ID")
    // are treated as distinct and dont toggle each other
    const itemKey = (i: TenantConfigViewItem) => `${i.dataIndex ?? ''}::${i.text ?? ''}`;
    const filteredByKey = new Map(filteredConfig.map((item) => [itemKey(item), item]));

    return baseConfig.map((item) => {
      const filteredItem = filteredByKey.get(itemKey(item));

      if (!filteredItem) {
        return item;
      }

      if (item.columns && item.columns.length > 0 && filteredItem.columns && filteredItem.columns.length > 0) {
        const updatedChildren = this.applyVisibilityToFilteredItems(item.columns, filteredItem.columns, isVisible);
        const anyVisible = updatedChildren.some((child) => this.itemVisible(child));
        const updatedParent: TenantConfigViewItem = {
          ...item,
          columns: updatedChildren,
          visible: anyVisible,
        };

        if ('hidden' in updatedParent) {
          updatedParent.hidden = !anyVisible;
        }

        return updatedParent;
      }

      return this.setItemVisibility(item, isVisible);
    });
  };

  onSelectAllFiltered = (isVisible: boolean) => {
    const filteredViewConfig = this.getFilteredViewConfig();
    this.setState(({ viewConfig }) => ({
      viewConfig: this.applyVisibilityToFilteredItems(viewConfig, filteredViewConfig, isVisible),
    }));
  };

  constructor(props: ViewConfiguratorModalProps) {
    super(props);
  }

  onOpen = async (): Promise<void> => {
    const gridApi = this.props.getGridApi?.();

    // this need to check the keyGroupedColumns & current column
    const useGroupedSnapshot =
      gridApi &&
      // !this.props.viewConfig.isDefault &&
      this.props.viewConfig.keyGroupedColumns &&
      !hasColumnOrderChanged(gridApi, this.props.viewConfig.keyGroupedColumns);
    const raw =
      // if we’ve already stored a grouped snapshot & equal to current grid column state then use it
      useGroupedSnapshot
        ? this.props.viewConfig.keyGroupedColumns
        : // else if it’s using columns, fallback to other logic
          Array.isArray(this.props.viewConfig.columns)
          ? this.props.viewConfig.columns
          : this.props.viewConfig.view;
    const viewColumnsConfig: TenantConfigViewItem[] = cloneDeep(raw || []);

    // Filter out columns that are fully hidden
    const visibleColumns = viewColumnsConfig.filter(
      (col) => col?.visible !== false && col?.hidden !== true && col?.hideFromConfigurator !== true,
    );
    // Group by dataIndex
    const groupedByDataIndex = groupBy(visibleColumns, 'dataIndex');

    // Find duplicates
    const duplicates = Object.entries(groupedByDataIndex).filter(([_, items]) => items.length > 1);
    // Log an error if duplicates exist
    if (duplicates.length > 0) {
      ServiceContainer.loggingService.warn(
        `Duplicate columns detected in view config by dataIndex: ${duplicates.map(([key]) => key).join(', ')}`,
      );
    }
    // Reorder the config based off the grid, so if the user makes changes via aggrid, they reflect here
    const reorderConfig: TenantConfigViewItem[] = [];
    if (this.props.getGridApi) {
      const gridApi = this.props.getGridApi();
      if (gridApi) {
        // If a config has nested columns, check if some were hidden in grid and keep them hidden/unhidden
        const agGridColumnsState = gridApi.getColumnState();
        // track what we have already placed (by key) so we don't place the same item twice
        const placedKeys = new Set<string>();
        const keyOf = (c: TenantConfigViewItem) => c.id ?? c.dataIndex ?? '';
        const sameKey = (a: TenantConfigViewItem, b: TenantConfigViewItem) => keyOf(a) && keyOf(a) === keyOf(b);
        const isCfgVisible = (c: TenantConfigViewItem) => c.visible !== false && c.hidden !== true;
        // match AG Grid duplicate colIds (with the _1, _2 suffixes) to config keys
        // suffix must be purely numeric so "dmd_r" does not match "dmd_r_csp", etc.
        // jsut pulled this up from below
        const matchesColId = (stateColId: string, key: string) => {
          if (stateColId === key) return true;
          if (!stateColId.startsWith(key + '_')) return false;
          const suffix = stateColId.slice(key.length + 1);
          return suffix.length > 0 && /^\d+$/.test(suffix);
        };

        // For duplicate-dataIndex groups that include a hideFromConfigurator entry (ex: "Group"),
        // identify which agGrid colIds belong to those hidden columns so they can be excluded
        // when selecting the right slot for each visible duplicate
        // AG-Grid assigns colIds in config definition order: 1st occurrence → exact dataIndex,
        // 2nd → dataIndex_1, 3rd → dataIndex_2, etc.
        const hideFromConfiguratorColIds: string[] = [];
        const viewColsByDataIndex = groupBy(viewColumnsConfig, (col) => col.dataIndex ?? '');
        Object.entries(viewColsByDataIndex).forEach(([dataIndex, cols]) => {
          if (cols.length <= 1 || !dataIndex) return;
          cols.forEach((col, defIdx) => {
            if (!col.hideFromConfigurator) return;
            hideFromConfiguratorColIds.push(defIdx === 0 ? dataIndex : `${dataIndex}_${defIdx}`);
          });
        });
        // Count non-hideFromConfigurator occurrences per key so each duplicate-dataIndex
        // column gets matched to its own distinct agGrid slot instead of all sharing one
        const keyOccurrenceCount: Record<string, number> = {};

        viewColumnsConfig
          .filter((viewColumnConfig) => !viewColumnConfig.hideFromConfigurator)
          .map((viewColumnConfig, i) => {
            // Determines if the current viewColumnConfig is present in grid columns state
            // in case the user made column changes within the grid before opening modal.
            // Those changes need to be reflected in the configurator modal.
            // Multiple items may be returned if there are columns with the same id/dataIndex value.
            // In the event multiple columns have identiy `dataIndex`s, ag-grid will append `_1`, `_2` etc.
            // to the duplicates.  Because of that, we match with matchesColId() so that e.g. "dmd_r"
            // does not accidentally match "dmd_r_csp".
            const colKey = viewColumnConfig.id ?? viewColumnConfig.dataIndex ?? '';
            // When a column has a separate `id` and `dataIndex`, ag-grid registers it by
            // dataIndex as colId. Fall back to dataIndex matching so the column lands at
            // its correct position rather than being appended to the end of reorderConfig.
            let agGridColumnMatches = agGridColumnsState.filter((gridColumn) =>
              colKey ? matchesColId(gridColumn.colId, colKey) : false,
            );
            if (
              agGridColumnMatches.length === 0 &&
              viewColumnConfig.dataIndex &&
              viewColumnConfig.dataIndex !== colKey
            ) {
              agGridColumnMatches = agGridColumnsState.filter((gridColumn) =>
                matchesColId(gridColumn.colId, viewColumnConfig.dataIndex!),
              );
            }

            // Finds current viewColumnConfig in the viewColumnsConfig.
            // Multiple items may be returned if there are columns with the same id/dataIndex value.
            const viewColumnMatches = viewColumnsConfig.filter(
              (viewColumn) => viewColumn.dataIndex === viewColumnConfig.dataIndex,
            );

            // Some view configs have a group property that shares the same dataIndex as another column in the config.
            // Finds correct config item in viewColumnConfigs that is not supposed to be hidden from the configurator.
            // This is most likely going to filter out the 'Group' column.
            const visibleViewColumnIndex =
              viewColumnMatches && viewColumnMatches.findIndex((a) => !a.hideFromConfigurator);

            // Exclude agGrid slots that belong to hideFromConfigurator columns (ex: "Group"),
            // then use occurrence count so each visible duplicate gets its own distinct slot
            const filteredMatches = agGridColumnMatches.filter((m) => !hideFromConfiguratorColIds.includes(m.colId));
            const occCount = keyOccurrenceCount[colKey] ?? 0;
            keyOccurrenceCount[colKey] = occCount + 1;
            const agGridColumn = filteredMatches[occCount] ?? agGridColumnMatches[visibleViewColumnIndex || 0];

            // Reorder columns based on the grid
            // If the viewConfigItem is found in the gridColumn's state, place it in the order based on grid
            if (agGridColumn) {
              const agGridColumnIndex = agGridColumnsState.indexOf(agGridColumn);
              const existingAtIndex = reorderConfig[agGridColumnIndex];
              // dont overwrite a visible twin with a config-hidden duplicate that shares the same key
              // this happens in the second loop below but we need it here as well to not resurect unwanted hidden duplicates
              if (
                !existingAtIndex || //same column id is hidden at placed index
                (!isCfgVisible(existingAtIndex) &&
                  isCfgVisible(viewColumnConfig) &&
                  sameKey(existingAtIndex, viewColumnConfig))
              ) {
                reorderConfig[agGridColumnIndex] = viewColumnConfig;
                const k = keyOf(viewColumnConfig);
                if (k) placedKeys.add(k);
              }
            }

            // If config item has columns has to be ordered differently
            const nested = viewColumnConfig.columns;
            if (!agGridColumn && nested && nested.length > 0) {
              //check if all child column is not visible
              const nestedColumns = nested.filter((col) => col.visible !== false);
              const nestedAgGridColumn =
                !isEmpty(nestedColumns) &&
                agGridColumnsState.find(
                  (a) => a.colId.replace(/_\d$/, '') == (nestedColumns[0].id || nestedColumns[0].dataIndex),
                );
              if (nestedAgGridColumn) {
                const nestedAgGridColumnIndex = agGridColumnsState.indexOf(nestedAgGridColumn);
                reorderConfig[nestedAgGridColumnIndex] = viewColumnConfig;
                const k = keyOf(viewColumnConfig);
                if (k) placedKeys.add(k);
              } else if (!isEmpty(nestedColumns)) {
                // Has visible children but no matching ag-grid slot — preserve relative order via i offset
                reorderConfig[i + 99] = viewColumnConfig;
              } else {
                // All children hidden — push to dense end so no sparse gap is created.
                // reorderItems will sort it to the absolute end since visible:false.
                reorderConfig.push(viewColumnConfig);
              }
            }

            // Determine and set column state for configurator,
            // visible determines the checkbox checked state.
            // Checking for hidden and visible in case config is setup with either or (we weren't consistent in config)
            // Skip visibility sync for grouped columns — ag-grid toggles their hide state based on
            // group expand/collapse, not user intent. Writing visible:false here would scatter them
            // to the end of the wrench list and break the group structure on apply.
            if (!viewColumnConfig.groupingKey) {
              if (agGridColumn && agGridColumn.hide) {
                viewColumnConfig.visible = false;
                viewColumnConfig.hidden = true;
              } else if (agGridColumn && !agGridColumn.hide && isNil(viewColumnConfig.visible)) {
                // Only sync to visible when the config has no explicit visibility decision
                // Pinned columns removed from column defs (visible:false) linger in getColumnState()
                // with stale hide:false — checking isNil prevents that from flipping them back to visible
                // basically if you hide a pinned column that has a duplicate dataindex - it can pop back in on fav save
                viewColumnConfig.visible = true;
              }
            }

            // Now that we get the actual state of the column per config property,
            // we can change things like pinned as well
            if (agGridColumn && !agGridColumn.pinned) {
              viewColumnConfig.pinned = undefined;
            } else if (agGridColumn && agGridColumn.pinned) {
              viewColumnConfig.pinned = 'left';
            }

            return viewColumnConfig;
          });

        // Insert skipped columns (e.g., with hideFromConfigurator) into reorderConfig
        // at their correct index from the current grid state instead of pushing them to the end.
        viewColumnsConfig.forEach((columnConfig) => {
          const key = keyOf(columnConfig);
          if (!key) return;

          // If we already placed this key in the first pass, don't place again
          if (placedKeys.has(key)) return;

          // If a grid-visible twin with the same key is already placed, skip grid-hidden duplicates
          if (
            !isCfgVisible(columnConfig) &&
            reorderConfig.some((c) => c && sameKey(c, columnConfig) && isCfgVisible(c))
          ) {
            return;
          }

          // Pick the best matching AG slot for this key (prefer pinned > visible > first)
          const candidateIdxs = agGridColumnsState
            .map((s, i) => ({ s, i }))
            .filter(({ s }) => matchesColId(s.colId, key))
            .map(({ i }) => i);

          const gridIndex =
            candidateIdxs.find((i) => agGridColumnsState[i].pinned) ??
            candidateIdxs.find((i) => !agGridColumnsState[i].hide) ??
            (candidateIdxs.length ? candidateIdxs[0] : -1);

          if (gridIndex !== -1) {
            const existing = reorderConfig[gridIndex];

            // Dont overwrite a grid-visible twin with a grid-hidden one
            if (existing && isCfgVisible(existing) && !isCfgVisible(columnConfig) && sameKey(existing, columnConfig)) {
              return;
            }

            reorderConfig[gridIndex] = columnConfig;
            placedKeys.add(key);
          } else {
            // No grid slot: append only if not a hidden dup of a visible twin
            if (
              !isCfgVisible(columnConfig) &&
              reorderConfig.some((c) => c && sameKey(c, columnConfig) && isCfgVisible(c))
            ) {
              return;
            }
            // do not push if already present by key (covers any earlier write)
            if (!reorderConfig.some((c) => c && sameKey(c, columnConfig))) {
              reorderConfig.push(columnConfig);
              placedKeys.add(key);
            }
          }
        });

        // Columns that share a dataIndex (ex: "Stylecolor" and "Stylecolor ID") both resolve
        // to the same AG Grid slot during the passes above, so only one gets placed and the
        // other is silently dropped. Re-append any missing non-hideFromConfigurator columns
        // using the original pre-mutation config values so they dont inherit incorrect
        // pinning or visibility from sibling duplicate-dataIndex columns (ex: hiding 1 of the above cols that was pinned - was forcing the second to pin after apply)
        viewColumnsConfig
          .filter((c) => !c.hideFromConfigurator && !reorderConfig.some((r) => r && r.text === c.text))
          .forEach((c) => {
            const original = (raw as TenantConfigViewItem[])?.find((r) => r.text === c.text);
            reorderConfig.push(original ? { ...original } : c);
          });
      }
    }

    const baseConfig = reorderConfig.length > 0 ? reorderConfig.filter((x) => x) : this.reorderItems(viewColumnsConfig);
    // apply the manual grouping
    const groupedConfig = this.reorderItems(applyGroupingInPlace(baseConfig));

    this.setState({
      viewConfig: groupedConfig,
    });
  };

  onClose = async () => {
    this.setState({
      openOverride: undefined,
    });
  };

  onCancel = () => {
    this.onClose();
  };

  onSubmit = async () => {
    // Default any column lacking `visible` to true, unless `hideFromConfigurator` is set or missing property visible in option
    const grouped = this.state.viewConfig.map((col) =>
      col.hideFromConfigurator || 'visible' in col || 'hidden' in col ? col : { ...col, visible: true },
    );

    const flat = flattenConfigItems(grouped);

    // start with basic new config, not overrwriting view & columns every time
    const newConfig: TenantConfigViewData = {
      ...this.props.viewConfig,
      isDefault: false,
    };

    // if this view originally had a columns array at the root, update that & store grouped format separately
    if (Array.isArray(this.props.viewConfig.columns)) {
      newConfig.columns = flat;
      newConfig.keyGroupedColumns = grouped;
    }
    // view with view array
    if (Array.isArray(this.props.viewConfig.view)) {
      // preserve exact nested structure
      newConfig.view = grouped;
    }

    // if there’s a grid.view, keep it in sync as before
    if (this.props.viewConfig.grid) {
      newConfig.grid = {
        ...this.props.viewConfig.grid,
        view: grouped,
      };
    }

    if (this.props.updateConfig) {
      this.props.updateConfig(newConfig);
    }
    if (this.props.onSubmit) {
      this.props.onSubmit(newConfig);
    }

    this.onClose();
  };

  onClickPinned = (item: TenantConfigViewItem) => {
    // during search, grouped items are copies not originals, so look up by dataIndex instead
    const index = this.state.viewConfig.findIndex((c) => c.dataIndex === item.dataIndex);
    item = this.state.viewConfig[index];
    if (!this.isPinned(item.pinned as string)) {
      item.pinned = 'left';
      if (item.columns) {
        item.columns = item.columns.map((x) => {
          return { ...x, pinned: 'left' };
        });
      }
    } else {
      item.pinned = undefined;
      if (item.columns) {
        item.columns = item.columns.map((x) => {
          return { ...x, pinned: undefined };
        });
      }
    }
    let updatedItems = clone(this.state.viewConfig); //.slice(0);
    updatedItems[index] = item;
    // updatedItems = this.reorderItems(updatedItems);
    // If it is newly pinned, add it to the front, if we removed pinning, move it to the first non pinned item
    // We don't need to reorder 1 or  fewer
    if (updatedItems.length > 2) {
      if (this.isPinned(item.pinned)) {
        updatedItems = without(updatedItems, item);
        updatedItems.unshift(item);
      } else {
        const firstItemNotPinnedIndex = updatedItems.findIndex((x) => item != x && !x.pinned) - 1;
        updatedItems = without(updatedItems, item);
        updatedItems.splice(firstItemNotPinnedIndex, 0, item);
      }
    }
    this.setState({
      viewConfig: updatedItems,
    });
  };

  isPinned = (pinned: string | undefined) => {
    return pinned === 'left' ? true : false;
  };

  onClickVisible = (item: TenantConfigViewItem) => {
    // Use dataIndex & text to find the original item in the full config, because during search the items are copies not originals
    const filteredItem = this.getFilteredViewConfig().find(
      (configItem) => configItem.dataIndex === item.dataIndex && configItem.text === item.text,
    );

    if (!filteredItem) {
      return;
    }

    const targetVisible = !this.itemVisibleInFilter(filteredItem);
    const updatedItems = this.applyVisibilityToFilteredItems(this.state.viewConfig, [filteredItem], targetVisible);

    // Move to bottom only if the item ended up fully hidden after the update
    // For group items under a filter, unchecking only hides the filtered children —
    // other children may still be visible, so the group should stay in place
    const itemIndex = updatedItems.findIndex(
      (configItem) => configItem.dataIndex === filteredItem.dataIndex && configItem.text === filteredItem.text,
    );
    if (itemIndex > -1 && !this.itemVisible(updatedItems[itemIndex])) {
      updatedItems.push(updatedItems.splice(itemIndex, 1)[0]);
    }

    if (filteredItem.columns && filteredItem.columns.length > 0) {
      this.setState({
        viewConfig: updatedItems,
      });
      return;
    }

    this.setState({
      viewConfig: updatedItems,
    });
  };

  onChildrenClickVisible = (parentDataIndex: string, childDataIndex: string) => {
    const filteredParent = this.getFilteredViewConfig().find((item) => item.dataIndex === parentDataIndex);
    const filteredChild = filteredParent?.columns?.find((child) => child.dataIndex === childDataIndex);

    if (!filteredParent || !filteredChild) {
      return;
    }

    const targetVisible = !this.itemVisible(filteredChild);

    this.setState(({ viewConfig }) => ({
      viewConfig: this.applyVisibilityToFilteredItems(
        viewConfig,
        [{ ...filteredParent, columns: [filteredChild] }],
        targetVisible,
      ),
    }));
  };

  reorderItems = (items: TenantConfigViewItem[]) => {
    return orderBy(
      items,
      [
        (i) => {
          if (i.visible === false || i.hidden === true) {
            return 1;
          }
          return 0;
        },
        (i) => (i.pinned ? 0 : 1),
      ],
      ['asc', 'asc'],
    );
  };

  onDragEnd = (result: DropResult) => {
    const { source, destination, type } = result;
    // dropped outside the list
    if (!destination) {
      return;
    }

    const reorder = (list: TenantConfigViewItem[], startIndex: number, endIndex: number): TenantConfigViewItem[] => {
      const updated = Array.from(list);
      const [removed] = updated.splice(startIndex, 1);
      updated.splice(endIndex, 0, removed);

      return updated;
    };

    const findIndexByKey = (items: TenantConfigViewItem[], key: string | undefined) => {
      return items.findIndex((item) => this.getConfigKey(item) === key);
    };

    // CHILD reordering
    if (type.startsWith('CHILD-')) {
      // extract the group's dataIndex
      const groupDataIndex = type.replace('CHILD-', '');
      // find that group in state
      const parentIndex = this.state.viewConfig.findIndex((item) => item.dataIndex === groupDataIndex);
      if (parentIndex < 0) return;

      const updated = cloneDeep(this.state.viewConfig);
      const children = updated[parentIndex].columns ?? [];
      const filteredParent = this.getFilteredViewConfig().find((item) => item.dataIndex === groupDataIndex);
      const filteredChildren = filteredParent?.columns ?? children;
      const sourceIndex = findIndexByKey(children, this.getConfigKey(filteredChildren[source.index]));
      const destinationIndex = findIndexByKey(children, this.getConfigKey(filteredChildren[destination.index]));

      if (sourceIndex < 0 || destinationIndex < 0) {
        return;
      }

      updated[parentIndex].columns = reorder(children, sourceIndex, destinationIndex);
      return this.setState({ viewConfig: updated });
    }

    const filteredViewConfig = this.getFilteredViewConfig();
    const sourceIndex = findIndexByKey(this.state.viewConfig, this.getConfigKey(filteredViewConfig[source.index]));
    const destinationIndex = findIndexByKey(
      this.state.viewConfig,
      this.getConfigKey(filteredViewConfig[destination.index]),
    );

    if (sourceIndex < 0 || destinationIndex < 0) {
      return;
    }

    let newConfig = reorder(this.state.viewConfig, sourceIndex, destinationIndex) as TenantConfigViewItem[];
    newConfig = orderBy(newConfig, 'pinned', 'asc');
    newConfig = orderBy(
      newConfig.map((x) => {
        if (x.visible) {
          delete x.visible;
          delete x.hidden;
        }
        return x;
      }),
      'visible',
      'desc',
    );

    this.setState({ viewConfig: newConfig });
  };

  showResetConfirmation = () => {
    const modal = document.body.querySelector<HTMLDivElement>('[class^=MuiDialog]');
    modal ? (modal.style.zIndex = '51') : undefined;
    this.setState({
      showResetConfirmation: true,
    });
  };

  hideResetConfirmation = () => {
    this.setState({
      showResetConfirmation: false,
    });
  };

  resetViewConfigurator = () => {
    // TODO:Create better type for updateConfig handler

    const updatedViewConfig = { ...this.props.unmodifiedViewDefn, isDefault: true };
    if (this.props.updateConfig) {
      this.props.updateConfig(updatedViewConfig);
    }
    if (this.props.onSubmit) {
      this.props.onSubmit(updatedViewConfig as TenantConfigViewData);
    }

    this.setState({
      showResetConfirmation: false,
      viewConfig:
        (updatedViewConfig.view as TenantConfigViewItem[]) || (updatedViewConfig.columns as TenantConfigViewItem[]),
      openOverride: false,
    });
  };

  itemVisible = (item: TenantConfigViewItem) => {
    return !isNil(item?.visible) ? item.visible : !isNil(item?.hidden) ? !item.hidden : true;
  };

  handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ searchValue: event.currentTarget.value });
  };

  handleClearSearch = () => {
    this.setState({ searchValue: '' });
  };

  getFilteredViewConfig = () => {
    const { viewConfig, searchValue } = this.state;
    const candidates = viewConfig.filter((item) => !item.hideFromConfigurator);
    const searchMatcher = this.getSearchMatcher(searchValue);

    if (!searchMatcher) {
      return candidates;
    }

    const { query, exact } = searchMatcher;

    return candidates.reduce<TenantConfigViewItem[]>((filteredItems, item) => {
      const itemMatches = this.itemMatchesSearch(item, query, exact);

      if (!item.columns || item.columns.length === 0) {
        if (itemMatches) {
          filteredItems.push(item);
        }

        return filteredItems;
      }

      const filteredChildren = item.columns.filter((child) => {
        if (child.hideFromConfigurator) {
          return false;
        }

        return itemMatches || this.itemMatchesSearch(child, query, exact);
      });

      if (!itemMatches && filteredChildren.length === 0) {
        return filteredItems;
      }

      filteredItems.push({
        ...item,
        columns: filteredChildren,
      });

      return filteredItems;
    }, []);
  };

  render() {
    const { showPinCheckboxForGrid } = this.props;
    const { showResetConfirmation, openOverride, searchValue } = this.state;
    const filteredViewConfig = this.getFilteredViewConfig();
    const filteredLeafItems = this.getFilteredLeafItems(filteredViewConfig);
    const allFilteredVisible =
      filteredLeafItems.length > 0 && filteredLeafItems.every((item) => this.itemVisible(item));
    const hasGroups = this.hasNestedColumns();
    return (
      <MuiThemeProvider theme={muiTheme}>
        {/* theme provider re-exported here because for reasons that are unclear, the theme doesn't apply witin this component correctly */}
        <TemplateModalWithButton
          buttonComponent={
            // TODO: INT-2114: Fix this properly
            <SubheaderQuickActionButton
              iconClass={'fas fa-wrench'}
              style={{ opacity: 1, position: 'relative' }}
              tooltipText="Configure metrics in this view"
              buttonDataQa="subheader-wrench-button"
            />
          }
          title={`Configure ${this.props.subheaderTitle} Metrics`}
          onOpen={this.onOpen}
          onSubmit={this.onSubmit}
          onCancel={this.onCancel}
          showSubmit={true}
          openOverride={openOverride}
          styleOverrides={{ maxWidth: '550px' }} // define a max width only in this location, so title text wraps
          leftAction={
            <SquareActionButton
              dataQa="wrench-reset-btn"
              className={resetButtonStyles}
              onClick={this.showResetConfirmation}
              text="RESET"
              type="primary"
            />
          }
        >
          <ConfirmationModal
            isOpen={showResetConfirmation}
            descriptionText="Are you sure you want to reset?"
            onConfirm={() => this.resetViewConfigurator()}
            onCancel={() => this.hideResetConfirmation()}
          />
          <div className={viewConfiguratorStyle} data-qa="wrench-modal-container">
            <div className="columns-title-container">
              <p>Metric</p>
              {showPinCheckboxForGrid && <p>Pin</p>}
              <p>Show</p>
            </div>
            <div className="search-container">
              <OutlinedInput
                aria-label="Search columns"
                className="wrench-search"
                fullWidth={true}
                inputProps={{
                  'data-qa': 'ViewConfiguratorSearchInput',
                }}
                endAdornment={
                  <IconButton size={'small'} onClick={this.handleClearSearch}>
                    <Close />
                  </IconButton>
                }
                value={searchValue}
                onChange={this.handleSearchChange}
                placeholder="Search columns... (= for exact)"
              />
              <div className="bulk-actions-container">
                <span>Select all</span>
                <label className="bulk-toggle">
                  <Checkbox checked={allFilteredVisible} onChange={(e) => this.onSelectAllFiltered(e.target.checked)} />
                </label>
              </div>
            </div>
            <div className="columns-grid">
              {filteredViewConfig && (
                <DragDropContext onDragEnd={this.onDragEnd}>
                  <Droppable droppableId="root" type="ROOT">
                    {/*eslint-disable-next-line @typescript-eslint/no-explicit-any*/}
                    {(provided: any, snapshot: any) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        style={getListStyle(snapshot.isDraggingOver, filteredViewConfig.length)}
                      >
                        {filteredViewConfig.map((item: TenantConfigViewItem, index: number) => {
                          if (item.hideFromConfigurator) {
                            return;
                          }
                          const checkboxState = this.getParentCheckboxState(item);
                          const id = (item.dataIndex || item.text) + index;
                          const hasChildren = !isNil(item.columns);
                          return (
                            <Draggable key={id} draggableId={id} index={index}>
                              {/*eslint-disable-next-line @typescript-eslint/no-explicit-any*/}
                              {(provided: any, snapshot: any) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                                  data-qa="menu-option"
                                >
                                  <StyledAccordion square={true}>
                                    <StyledAccordionSummary
                                      expandIcon={hasChildren ? <ExpandMore /> : null}
                                      style={{
                                        backgroundColor: snapshot.isDragging ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.05)',
                                        cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                                      }}
                                    >
                                      <StyledTypography variant="body2" noWrap={true} hasIcon={hasChildren}>
                                        <Tooltip title={item.text}>
                                          <span data-qa="view-config-metric-title">
                                            {item.viewConfiguratorName || item.text}
                                          </span>
                                        </Tooltip>
                                      </StyledTypography>

                                      <div className="checkbox-container">
                                        {showPinCheckboxForGrid && (
                                          <Checkbox
                                            title={'Pinned?'}
                                            checked={this.isPinned(item.pinned as string)}
                                            onClick={(e) => {
                                              // prevent the checkbox click from also toggling the accordion
                                              e.stopPropagation();
                                              this.onClickPinned(item);
                                            }}
                                          />
                                        )}
                                        <Checkbox
                                          title={'Visible?'}
                                          checked={checkboxState.checked}
                                          indeterminate={checkboxState.indeterminate}
                                          onClick={(e) => {
                                            // prevent the checkbox click from also toggling the accordion
                                            e.stopPropagation();
                                            this.onClickVisible(item);
                                          }}
                                        />
                                      </div>
                                    </StyledAccordionSummary>
                                    <StyledAccordionDetails>
                                      {hasChildren && (
                                        <Droppable
                                          droppableId={`child-group-${item.dataIndex}`}
                                          type={`CHILD-${item.dataIndex}`}
                                        >
                                          {(childProv: any) => {
                                            return (
                                              <div ref={childProv.innerRef} {...childProv.droppableProps}>
                                                {item.columns?.map((col, idx) => (
                                                  <Draggable
                                                    key={`child-${item.dataIndex}-${col.dataIndex}`}
                                                    draggableId={`child-${item.dataIndex}-${col.dataIndex}`}
                                                    index={idx}
                                                  >
                                                    {(colProv, colSnapshot) => (
                                                      <div
                                                        ref={colProv.innerRef}
                                                        {...colProv.draggableProps}
                                                        {...colProv.dragHandleProps}
                                                        style={{
                                                          ...getItemStyle(
                                                            colSnapshot.isDragging,
                                                            colProv.draggableProps.style,
                                                          ),
                                                          display: 'grid',
                                                          marginLeft: 0,
                                                          marginRight: 0,
                                                          padding: '4px 0px 4px 8px',
                                                        }}
                                                        className="child-metric-container"
                                                      >
                                                        <StyledTypography hasIcon={hasChildren} noWrap variant="body2">
                                                          <Tooltip title={col.text}>
                                                            <span>{col.viewConfiguratorName || col.text}</span>
                                                          </Tooltip>
                                                        </StyledTypography>
                                                        <Checkbox
                                                          checked={this.itemVisible(col)}
                                                          onClick={() =>
                                                            this.onChildrenClickVisible(item.dataIndex, col.dataIndex)
                                                          }
                                                        />
                                                      </div>
                                                    )}
                                                  </Draggable>
                                                ))}
                                                {childProv.placeholder}
                                              </div>
                                            );
                                          }}
                                        </Droppable>
                                      )}
                                    </StyledAccordionDetails>
                                  </StyledAccordion>
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </div>
          </div>
        </TemplateModalWithButton>
      </MuiThemeProvider>
    );
  }
}
